import React, { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import CoursesProgramm from './CoursesProgramm';
import { useSelector } from 'react-redux';
import { LoadingContext } from '../../composants/loadingContext';
import { useTitle } from '../../configurations/utils/Utils';
import axiosInstance from '../../configurations/axiosInstance/AxiosInstance';
import { toastClick } from '../../composants/toast/CustomToast';
import { formatTitle, getName } from '../../composants/utils/utils';
import { 
    faUserCircle, 
    faArrowLeft, 
    faClock, 
    faBookOpen, 
    faGraduationCap,
    faPlay,
    faChevronRight,
    faStar,
    faUsers,
    faAward,
    faChartLine,
    faCalendarAlt,
    faFileAlt,
    faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Loader from '../../composants/loader/Loader';
import { Tag, Collapse, Empty } from 'antd';

const { Panel } = Collapse;

const CoursesDetail = () => {
    const [course, setCourse] = useState(null);
    const {id} = useParams();
    const {accessToken} = useSelector(state => state.authUser);
    const {loading, setLoading} = useContext(LoadingContext);
    useTitle("Détails de la Formation");
    const enrollement = ()=>{
        axiosInstance(accessToken).post('api/learn/enrollments/enroll?id='+id).then(
            (response)=>{
                toastClick('Inscription réussie','success');
                window.location.href="/learn/"+id+"/"+response.data.id;
            }
        ).catch((error)=>{
            toastClick('Erreur lors de l\'inscription','error');
        }); 
    }

    const findFormationById = ()=>{
        setLoading(true);
        axiosInstance(accessToken).get('public/api/learn/formations/published/'+id).then(
            (response)=>{
                if(response.data!=null){
                    setCourse(response.data);
                }else{
                    toastClick('Formation non trouvée','info')
                    window.location.href="/";
                }
                setLoading(false);
            }
        ).catch((error)=>{
            setLoading(false);
            toastClick('Formation non trouvée','info')
            window.location.href="/";
        });
    }

    const getLevelLabel = (niveau) => {
        const levels = {
            BEGINNER: 'Débutant',
            INTERMEDIATE: 'Intermédiaire',
            ADVANCED: 'Avancé',
            EXPERT: 'Expert'
        };
        return levels[niveau] || niveau;
    };

    const getLevelColor = (niveau) => {
        const colors = {
            BEGINNER: 'green',
            INTERMEDIATE: 'blue',
            ADVANCED: 'orange',
            EXPERT: 'red'
        };
        return colors[niveau] || 'default';
    };

    const getTotalChapitres = () => {
        if (!course?.modules) return 0;
        return course.modules.reduce((total, module) => {
            return total + (module.chapitres?.length || 0);
        }, 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    };

    useEffect(()=>{
        findFormationById()
    },[])
    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            {loading && <Loader />}

            {/* Back Button */}
            <div className="bg-white shadow-sm border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-4 py-4">
                    <button 
                        onClick={()=>{window.location.href="/"}}  
                        className="inline-flex items-center gap-2 text-gray-700 hover:text-green-600 font-semibold transition-colors duration-300 group"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="group-hover:-translate-x-1 transition-transform duration-300" />
                        <span>Retour aux formations</span>
                    </button>
                </div>
            </div>

            {course != null && (
                <div className="max-w-7xl mx-auto px-4 py-8">
                    {/* Hero Section */}
                    <div className="bg-gradient-to-r from-green-500 to-blue-500 rounded-3xl shadow-2xl overflow-hidden mb-8">
                        <div className="flex flex-col lg:flex-row">
                            {/* Left Content */}
                            <div className="lg:w-3/5 p-8 lg:p-12 text-white">
                                {/* Category & Level Badges */}
                                <div className="flex flex-wrap gap-3 mb-6">
                                    <span className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold uppercase tracking-wide">
                                        {course.categories?.description || 'Formation'}
                                    </span>
                                    <span className={`bg-${getLevelColor(course.niveau)}-500/30 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold`}>
                                        {getLevelLabel(course.niveau)}
                                    </span>
                                    {course.withFinalQuiz && (
                                        <span className="bg-yellow-400/30 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2">
                                            <FontAwesomeIcon icon={faAward} />
                                            Quiz final
                                        </span>
                                    )}
                                </div>

                                {/* Title */}
                                <h1 className="text-3xl lg:text-5xl font-bold mb-4 leading-tight">
                                    {course.titre}
                                </h1>

                                {/* Description */}
                                {course.description && (
                                    <p className="text-lg text-white/90 mb-6 leading-relaxed">
                                        {course.description}
                                    </p>
                                )}

                                {/* Stats Quick View */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                                        <FontAwesomeIcon icon={faBookOpen} className="text-2xl mb-2" />
                                        <div className="font-bold text-xl">{course.nombreModules || 0}</div>
                                        <div className="text-xs text-white/70">Modules</div>
                                    </div>
                                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                                        <FontAwesomeIcon icon={faFileAlt} className="text-2xl mb-2" />
                                        <div className="font-bold text-xl">{getTotalChapitres()}</div>
                                        <div className="text-xs text-white/70">Chapitres</div>
                                    </div>
                                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                                        <FontAwesomeIcon icon={faClock} className="text-2xl mb-2" />
                                        <div className="font-bold text-xl">{course.dureeEstimee || 0}h</div>
                                        <div className="text-xs text-white/70">Durée</div>
                                    </div>
                                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                                        <FontAwesomeIcon icon={faUsers} className="text-2xl mb-2" />
                                        <div className="font-bold text-xl">{course.nombreInscrits || 0}</div>
                                        <div className="text-xs text-white/70">Inscrits</div>
                                    </div>
                                </div>

                                {/* CTA Button */}
                                {course.modules && course.modules.length > 0 && (
                                    <Link 
                                        onClick={()=>{enrollement()}}
                                        className="inline-flex items-center gap-3 bg-white text-green-600 font-bold py-4 px-8 rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                                    >
                                        <FontAwesomeIcon icon={faPlay} className="text-xl" />
                                        <span className="text-lg">Commencer la formation</span>
                                        <FontAwesomeIcon icon={faChevronRight} />
                                    </Link>
                                )}
                            </div>

                            {/* Right Image */}
                            <div className="lg:w-2/5 relative">
                                <div className="aspect-square lg:aspect-auto lg:h-full relative">
                                    <img 
                                        src={course.imageCouverture || "/images/Best-online-course-platforms.png"}
                                        alt={course.titre}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.target.src = "/images/Best-online-course-platforms.png";
                                        }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent lg:hidden"></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Info Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                        <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-green-500 hover:shadow-xl transition-shadow duration-300">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                                    <FontAwesomeIcon icon={faBookOpen} className="text-2xl text-green-600" />
                                </div>
                                <div>
                                    <p className="text-gray-500 text-sm">Modules</p>
                                    <p className="text-2xl font-bold text-gray-900">{course.nombreModules || 0}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-blue-500 hover:shadow-xl transition-shadow duration-300">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                                    <FontAwesomeIcon icon={faClock} className="text-2xl text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-gray-500 text-sm">Durée estimée</p>
                                    <p className="text-2xl font-bold text-gray-900">{course.dureeEstimee || 0}h</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-purple-500 hover:shadow-xl transition-shadow duration-300">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center">
                                    <FontAwesomeIcon icon={faChartLine} className="text-2xl text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-gray-500 text-sm">Niveau</p>
                                    <p className="text-lg font-bold text-gray-900">{getLevelLabel(course.niveau)}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Additional Info */}
                    {course.dateCreation && (
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8 rounded-lg">
                            <div className="flex items-center gap-3">
                                <FontAwesomeIcon icon={faCalendarAlt} className="text-blue-600 text-xl" />
                                <div>
                                    <p className="text-sm text-gray-600">Créée le</p>
                                    <p className="font-semibold text-gray-900">{formatDate(course.dateCreation)}</p>
                                </div>
                                {course.dateModification && course.dateModification !== course.dateCreation && (
                                    <>
                                        <div className="mx-4 h-8 w-px bg-gray-300"></div>
                                        <div>
                                            <p className="text-sm text-gray-600">Dernière mise à jour</p>
                                            <p className="font-semibold text-gray-900">{formatDate(course.dateModification)}</p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Program Section */}
                    <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-12">
                        <div className="mb-8">
                            <h2 className="text-3xl font-bold text-gray-900 mb-2">Programme de la formation</h2>
                            <p className="text-gray-600">Découvrez le contenu détaillé organisé par modules et chapitres</p>
                        </div>

                        {course.modules && course.modules.length > 0 ? (
                            <>
                                <Collapse 
                                    defaultActiveKey={['0']} 
                                    className="bg-gray-50 border-0"
                                    expandIconPosition="end"
                                >
                                    {course.modules.map((module, index) => (
                                        <Panel
                                            key={index}
                                            header={
                                                <div className="flex items-center justify-between pr-4">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-blue-500 text-white rounded-lg flex items-center justify-center font-bold">
                                                            {module.ordre || index + 1}
                                                        </div>
                                                        <div>
                                                            <h3 className="text-lg font-bold text-gray-900">{module.titre}</h3>
                                                            {module.description && (
                                                                <p className="text-sm text-gray-600 mt-1">{module.description}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-4 text-sm text-gray-500">
                                                        <span className="flex items-center gap-1">
                                                            <FontAwesomeIcon icon={faFileAlt} className="text-blue-500" />
                                                            {module.chapitres?.length || 0} chapitre{(module.chapitres?.length || 0) > 1 ? 's' : ''}
                                                        </span>
                                                        {module.dureeEstimee && (
                                                            <span className="flex items-center gap-1">
                                                                <FontAwesomeIcon icon={faClock} className="text-green-500" />
                                                                {module.dureeEstimee}h
                                                            </span>
                                                        )}
                                                        {module.withQuiz && (
                                                            <Tag color="gold" className="flex items-center gap-1">
                                                                <FontAwesomeIcon icon={faAward} />
                                                                Quiz
                                                            </Tag>
                                                        )}
                                                    </div>
                                                </div>
                                            }
                                            className="mb-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 border-0"
                                        >
                                            {module.chapitres && module.chapitres.length > 0 ? (
                                                <div className="space-y-3 pt-4">
                                                    {module.chapitres.map((chapitre, chIndex) => (
                                                        <div 
                                                            key={chapitre.id} 
                                                            className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors duration-200 border border-gray-200 hover:border-green-300"
                                                        >
                                                            <div className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-semibold text-sm">
                                                                {chapitre.ordre || chIndex + 1}
                                                            </div>
                                                            <div className="flex-1">
                                                                <h4 className="font-semibold text-gray-900">{chapitre.titre}</h4>
                                                                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                                                    {chapitre.dureeEstimee > 0 && (
                                                                        <span className="flex items-center gap-1">
                                                                            <FontAwesomeIcon icon={faClock} />
                                                                            {chapitre.dureeEstimee} min
                                                                        </span>
                                                                    )}
                                                                    {chapitre.videoUrl && (
                                                                        <span className="text-blue-600 flex items-center gap-1">
                                                                            <FontAwesomeIcon icon={faPlay} />
                                                                            Vidéo
                                                                        </span>
                                                                    )}
                                                                    {chapitre.documentUrl && (
                                                                        <span className="text-purple-600 flex items-center gap-1">
                                                                            <FontAwesomeIcon icon={faFileAlt} />
                                                                            Document
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <FontAwesomeIcon icon={faCheckCircle} className="text-gray-300 text-xl" />
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center py-8 text-gray-500">
                                                    <FontAwesomeIcon icon={faFileAlt} className="text-4xl mb-3 text-gray-300" />
                                                    <p>Aucun chapitre disponible pour ce module</p>
                                                </div>
                                            )}
                                        </Panel>
                                    ))}
                                </Collapse>

                                {/* Final Quiz Section */}
                                {course.withFinalQuiz && (
                                    <div className="mt-6 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-2xl p-6 shadow-lg">
                                        <div className="flex items-start gap-4">
                                            <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-orange-500 text-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                                                <FontAwesomeIcon icon={faAward} className="text-2xl" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <h3 className="text-xl font-bold text-gray-900">Quiz Final d'Évaluation</h3>
                                                    <Tag color="gold" className="font-semibold">Obligatoire</Tag>
                                                </div>
                                                <p className="text-gray-700 mb-4 leading-relaxed">
                                                    Cette formation comprend un quiz final obligatoire pour valider vos connaissances. 
                                                    Vous devrez réussir ce quiz pour obtenir votre certificat de formation.
                                                </p>
                                                <div className="flex flex-wrap gap-4 text-sm">
                                                    <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                                                        <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
                                                        <span className="font-semibold text-gray-700">Validez vos compétences</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                                                        <FontAwesomeIcon icon={faGraduationCap} className="text-blue-600" />
                                                        <span className="font-semibold text-gray-700">Obtenez votre certificat</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                                                        <FontAwesomeIcon icon={faStar} className="text-yellow-600" />
                                                        <span className="font-semibold text-gray-700">Prouvez votre expertise</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-16">
                                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <FontAwesomeIcon icon={faBookOpen} className="text-5xl text-gray-400" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucun module disponible</h3>
                                <p className="text-gray-600">Le contenu de cette formation sera bientôt disponible.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CoursesDetail;